import { Tabs } from "@base-ui/react/tabs";
import { OCCASION_CONFIG } from "@listou/shared/constants";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { AppearanceTab } from "../components/editor/appearance-tab";
import { AssistantPanel } from "../components/editor/assistant-panel";
import { GiftsTab } from "../components/editor/gifts-tab";
import { SettingsTab } from "../components/editor/settings-tab";
import { ShareTab } from "../components/editor/share-tab";
import { WishesTab } from "../components/editor/wishes-tab";
import { ListMiniCover } from "../components/list-mini-cover";
import { PageLoader } from "../components/ui/spinner";
import { ApiError } from "../lib/api";
import { countdownLabel, formatDay } from "../lib/format";
import { listQuery, wishesQuery } from "../lib/queries";
import { errorMessage } from "../lib/session";

const TABS = [
  { id: "envies", label: "Envies" },
  { id: "page", label: "Page" },
  { id: "partage", label: "Partage" },
  { id: "reservations", label: "Réservations" },
  { id: "reglages", label: "Réglages" },
] as const;

type TabId = (typeof TABS)[number]["id"];

export const Route = createFileRoute("/_authed/mes-listes/$listId")({
  // The tab lives in the URL so a refresh, a shared link or the back button lands on it.
  validateSearch: (search: Record<string, unknown>): { tab?: TabId } => ({
    tab: TABS.some((tab) => tab.id === search.tab) ? (search.tab as TabId) : undefined,
  }),
  // prefetchQuery never throws: a missing list lands in the query cache as an error, and
  // the page renders its own message for it.
  loader: ({ context: { queryClient }, params: { listId } }) =>
    Promise.all([
      queryClient.prefetchQuery(listQuery(listId)),
      queryClient.prefetchQuery(wishesQuery(listId)),
    ]),
  component: ListEditorPage,
});

function ListEditorPage() {
  const { listId } = Route.useParams();
  const { tab = "envies" } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const list = useQuery(listQuery(listId));
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [seed, setSeed] = useState<{ value: string; nonce: number } | null>(null);

  const closeAssistant = useCallback(() => setAssistantOpen(false), []);

  if (list.isPending) return <PageLoader />;
  if (list.isError) {
    const missing = list.error instanceof ApiError && list.error.status === 404;
    return (
      <div className="mx-auto grid max-w-6xl justify-items-start gap-4 px-4 py-12 sm:px-6">
        <h1 className="text-3xl font-semibold">
          {missing ? "Cette liste n'est plus accessible" : "La liste n'a pas pu être chargée"}
        </h1>
        <p className="text-muted">
          {missing
            ? "Elle a été supprimée, ou vous n'en faites plus partie."
            : errorMessage(list.error, "Réessayez dans un instant.")}
        </p>
        <Link to="/mes-listes" className="btn">
          Retour à mes listes
        </Link>
      </div>
    );
  }

  const data = list.data.list;
  const occasion = OCCASION_CONFIG[data.occasion];
  const countdown = countdownLabel(data.eventDate);

  return (
    <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 py-6 sm:px-6 sm:py-10">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <ListMiniCover list={data} className="h-16 w-20 shrink-0 rounded-2xl" />
          <div className="grid min-w-0 gap-1">
            <h1 className="truncate text-3xl font-semibold sm:text-4xl">{data.title}</h1>
            <p className="text-muted">
              {occasion.label}
              {data.eventDate && `, le ${formatDay(data.eventDate)}`}
              {countdown && <span className="ml-2 font-bold text-ink">{countdown}</span>}
              {data.archived && <span className="ml-2 font-bold">(archivée)</span>}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* /l/:slug refuses a member in surprise mode; the preview never shows reservations then. */}
          <Link to="/aide" hash="creer" className="btn-ghost">
            Aide
          </Link>
          <Link to="/mes-listes/$listId/preview" params={{ listId }} className="btn-secondary">
            Voir la page
          </Link>
          <Link to="." search={{ tab: "partage" }} className="btn">
            Partager
          </Link>
        </div>
      </header>

      <Tabs.Root
        value={tab}
        onValueChange={(value) => void navigate({ search: { tab: value as TabId } })}
      >
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <Tabs.List
            aria-label="Sections de la liste"
            className="relative flex w-max gap-1 rounded-full bg-ink/[0.06] p-1"
          >
            {TABS.map((item) => (
              <Tabs.Tab
                key={item.id}
                value={item.id}
                className="relative z-10 min-h-11 rounded-full px-5 font-bold text-muted transition outline-none hover:text-ink focus-visible:outline-3 focus-visible:outline-accent data-[selected]:text-ink"
              >
                {item.label}
              </Tabs.Tab>
            ))}
            <Tabs.Indicator className="absolute top-1 left-0 z-0 h-[calc(100%-0.5rem)] w-[var(--active-tab-width)] translate-x-[var(--active-tab-left)] rounded-full bg-surface shadow-sm transition-all duration-200" />
          </Tabs.List>
        </div>

        <Tabs.Panel value="envies" className="pt-6 outline-none">
          <WishesTab list={data} seed={seed} onAskAssistant={() => setAssistantOpen(true)} />
        </Tabs.Panel>
        <Tabs.Panel value="page" className="pt-6 outline-none">
          <AppearanceTab list={data} />
        </Tabs.Panel>
        <Tabs.Panel value="partage" className="pt-6 outline-none">
          <ShareTab list={data} />
        </Tabs.Panel>
        <Tabs.Panel value="reservations" className="pt-6 outline-none">
          <GiftsTab list={data} />
        </Tabs.Panel>
        <Tabs.Panel value="reglages" className="pt-6 outline-none">
          <SettingsTab list={data} />
        </Tabs.Panel>
      </Tabs.Root>

      <AssistantPanel
        list={data}
        open={assistantOpen}
        onClose={closeAssistant}
        onSearch={(value) => {
          setSeed({ value, nonce: Date.now() });
          setAssistantOpen(false);
          void navigate({ search: { tab: "envies" } });
        }}
      />
    </div>
  );
}
